import { useCallback, useEffect, useRef } from "react";
import { startActivitySession, endActivitySession, recordItemEvent } from "@music-app/persistence";

export type ExerciseSessionMode = "drill" | "practice";

/**
 * Tracks a Drill or Practice mode visit as an activity_session with per-item
 * item_events. Starts on mount when `enabled` is true; ends on cleanup or when
 * `enabled` becomes false.
 *
 * Returns `recordItem` — call this once per item heard in Drill (ungraded)
 * mode to write an item_event for exposure-threshold tracking. nodeId is
 * derived automatically from the itemId prefix.
 *
 * Practice mode (graded) does not call recordItem; FSRS reviews provide the
 * signal instead.
 */
export function useExerciseSession(
  skillId: string,
  mode: ExerciseSessionMode,
  enabled: boolean,
): { recordItem: (itemId: string) => void } {
  const sessionIdRef = useRef<string | null>(null);
  const itemCountRef = useRef(0);

  const recordItem = useCallback((itemId: string) => {
    const id = sessionIdRef.current;
    if (!id || mode !== "drill") return;
    itemCountRef.current += 1;
    void recordItemEvent(id, itemId, skillId, "drill");
  }, [skillId, mode]);

  useEffect(() => {
    if (!enabled) return;
    itemCountRef.current = 0;
    let active = true;
    startActivitySession(mode, null, skillId).then((id) => {
      if (active) {
        sessionIdRef.current = id;
      } else {
        void endActivitySession(id, 0);
      }
    });
    return () => {
      active = false;
      if (sessionIdRef.current) {
        void endActivitySession(sessionIdRef.current, itemCountRef.current);
        sessionIdRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, skillId, mode]);

  return { recordItem };
}
